/**
 * Transporte loopback in-process (netcode, tests, dev). Determinista y SIN timers.
 *
 * Modela un enlace real: latencia por ticks y, opcionalmente, **pérdida y
 * reordenamiento** (como UDP / WebTransport datagrams) para verificar que el
 * netcode tolera paquetes perdidos y desordenados. El cliente envía sus inputs
 * con **redundancia** (los últimos K) para que un datagrama perdido no pierda un
 * input; el servidor deduplica por tick.
 */
import { InputState, MapConfig, MatchRules } from "../game";
import { InputCommand, Snapshot, SNAPSHOT_INTERVAL_TICKS } from "./protocol";
import { AuthoritativeServer } from "./server";
import { PredictiveClient } from "./client";

export interface LoopbackOptions {
  players?: 1 | 2;
  bot?: boolean;
  map?: MapConfig;
  rules?: MatchRules;
  /** Probabilidad de pérdida por paquete (0..1). Default 0. */
  loss?: number;
  /** Jitter máximo de reordenamiento, en ticks. Default 0. */
  reorderJitter?: number;
  /** Inputs por paquete (redundancia ante pérdida). Default 1. */
  inputRedundancy?: number;
  /** Buffer de jitter (ticks): el servidor va por detrás del cliente para que
   *  los inputs (y sus copias redundantes) lleguen a tiempo. Default 0. */
  inputBuffer?: number;
  /** Semilla del RNG de pérdida/jitter (reproducibilidad en tests). */
  seed?: number;
}

/** RNG determinista (mulberry32) para pérdida/jitter reproducibles. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

interface LineOptions {
  loss?: number;
  reorderJitter?: number;
  rng?: () => number;
}

/** Cola con retardo en ticks; puede perder y reordenar paquetes (simula UDP). */
export class DelayLine<T> {
  private queue: { deliverAt: number; msg: T }[] = [];
  private readonly loss: number;
  private readonly jitter: number;
  private readonly rng: () => number;

  constructor(public readonly delayTicks: number, opts: LineOptions = {}) {
    this.loss = opts.loss ?? 0;
    this.jitter = opts.reorderJitter ?? 0;
    this.rng = opts.rng ?? (() => 0);
  }

  send(msg: T, nowTick: number): void {
    if (this.loss > 0 && this.rng() < this.loss) return; // paquete perdido
    const extra = this.jitter > 0 ? Math.floor(this.rng() * (this.jitter + 1)) : 0;
    this.queue.push({ deliverAt: nowTick + this.delayTicks + extra, msg });
  }

  receive(nowTick: number): T[] {
    const ready: { deliverAt: number; msg: T }[] = [];
    const keep: { deliverAt: number; msg: T }[] = [];
    for (const e of this.queue) (e.deliverAt <= nowTick ? ready : keep).push(e);
    this.queue = keep;
    ready.sort((a, b) => a.deliverAt - b.deliverAt); // el jitter puede reordenar
    return ready.map((e) => e.msg);
  }
}

/**
 * Sesión completa cliente↔servidor sobre loopback. Un `advance(input)` = un tick:
 * el cliente predice y envía (con redundancia), la red entrega (con retardo y
 * quizá pérdida/reorden), el servidor simula y el snapshot vuelve para reconciliar.
 */
export class LoopbackSession {
  readonly server: AuthoritativeServer;
  readonly client: PredictiveClient;
  private readonly up: DelayLine<InputCommand[]>;
  private readonly down: DelayLine<Snapshot>;
  private readonly redundancy: number;
  private readonly serverLag: number;
  private readonly recent: InputCommand[] = [];
  private clock = 0;

  /** `latencyTicks` es el retardo one-way (RTT ≈ 2×). 60 Hz → 3 ticks ≈ 50 ms. */
  constructor(latencyTicks = 0, opts: LoopbackOptions = {}) {
    const players = opts.players ?? 1;
    this.server = new AuthoritativeServer({ players, bot: opts.bot, map: opts.map, rules: opts.rules });
    this.client = new PredictiveClient({ players, localSlot: 0, map: opts.map, rules: opts.rules });
    this.redundancy = Math.max(1, opts.inputRedundancy ?? 1);
    this.serverLag = latencyTicks + (opts.inputBuffer ?? 0);
    const seed = opts.seed ?? 0x9e3779b9;
    const line: LineOptions = { loss: opts.loss, reorderJitter: opts.reorderJitter };
    this.up = new DelayLine(latencyTicks, { ...line, rng: mulberry32(seed) });
    this.down = new DelayLine(latencyTicks, { ...line, rng: mulberry32(seed ^ 0x5bd1e995) });
  }

  advance(input: InputState): void {
    // Cliente → predice y envía los últimos K inputs (redundancia).
    const cmd = this.client.predict(input);
    this.recent.push(cmd);
    while (this.recent.length > this.redundancy) this.recent.shift();
    this.up.send(this.recent.slice(), this.clock);

    // Red → servidor (deduplica por tick; los redundantes ya aplicados se descartan).
    for (const batch of this.up.receive(this.clock)) {
      for (const c of batch) this.server.receiveInput(c);
    }

    // El servidor va `serverLag` ticks por detrás del cliente (buffer de jitter):
    // así los inputs, aunque lleguen con retardo/reordenados, están disponibles
    // en el tick que el servidor simula.
    if (this.clock >= this.serverLag) {
      this.server.step();
      if (this.server.currentTick % SNAPSHOT_INTERVAL_TICKS === 0) {
        this.down.send(this.server.snapshot(), this.clock);
      }
    }
    for (const s of this.down.receive(this.clock)) this.client.reconcile(s);

    this.clock++;
  }
}
